import { useState } from "react";
import { Link } from "react-router-dom";
import { Button, ErrorText, Field, Panel, SelectField, Spinner } from "../../components/ui";
import { coverImage } from "../feed/api";
import { useFeed, useMyInterests } from "../feed/hooks";
import { fmtAmount, fmtPct, fmtRate } from "../../lib/format";
import { PORTFOLIO_KIND_LABEL, type PortfolioKind } from "./api";
import { useCreatePortfolio, usePortfolios } from "./hooks";

function OpportunityBubbles() {
  const { data } = useFeed(); // already ordered most-recent-first (publishedAt desc)
  const interests = useMyInterests();
  if (!data || data.length === 0) return null;

  const interestedIds = new Set(interests.data?.map((i) => i.opportunityId));

  return (
    <Panel title="Opportunités">
      <div className="opportunity-bubbles">
        {data.map((o) => {
          const cover = coverImage(o);
          const already = interestedIds.has(o.id);
          return (
            <Link
              key={o.id}
              to={`/opportunities/${o.id}`}
              className="opportunity-bubble"
              title={o.title}
            >
              <span className={`opportunity-bubble-img ${already ? "no-ring" : ""}`}>
                {cover ? <img src={cover} alt="" /> : o.title.slice(0, 2).toUpperCase()}
              </span>
            </Link>
          );
        })}
      </div>
    </Panel>
  );
}

export function PortfoliosPage() {
  const { data, isLoading, error } = usePortfolios();
  const create = useCreatePortfolio();
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [kind, setKind] = useState<PortfolioKind>("sgi");
  const [targetReturn, setTargetReturn] = useState("");

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || !targetReturn) return;
    create.mutate(
      { name: name.trim(), kind, targetReturn: targetReturn.replace(",", ".") },
      {
        onSuccess: () => {
          setCreating(false);
          setName("");
          setKind("sgi");
          setTargetReturn("");
        },
      },
    );
  }

  return (
    <>
      <OpportunityBubbles />

      <Panel
        title="Mes portefeuilles"
        actions={
          <Button variant="ghost" onClick={() => setCreating((v) => !v)}>
            {creating ? "Annuler" : "+ Portefeuille"}
          </Button>
        }
      >
        {creating && (
          <form className="inline-form" onSubmit={submit}>
            <Field label="Nom" value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
            <SelectField
              label="Type"
              value={kind}
              onChange={(e) => setKind(e.target.value as PortfolioKind)}
            >
              {(Object.keys(PORTFOLIO_KIND_LABEL) as PortfolioKind[]).map((k) => (
                <option key={k} value={k}>
                  {PORTFOLIO_KIND_LABEL[k]}
                </option>
              ))}
            </SelectField>
            <Field
              label="Rendement cible (%/an)"
              inputMode="decimal"
              placeholder="ex. 8,5"
              pattern="[0-9]+([.,][0-9]+)?"
              value={targetReturn}
              onChange={(e) => setTargetReturn(e.target.value)}
              required
            />
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? "…" : "Créer"}
            </Button>
            <ErrorText error={create.error} />
          </form>
        )}

        <ErrorText error={error} />
        {isLoading ? (
          <Spinner />
        ) : (
          data?.map((p) => {
            const pnl = Number(p.summary.unrealized_pnl) + Number(p.summary.realized_pnl);
            return (
              <Link key={p.id} to={`/portfolios/${p.id}`} className="portfolio-card">
                <div className="portfolio-card-row">
                  <strong>
                    {p.name} <span className="kind-badge">{p.kind.toUpperCase()}</span>
                  </strong>
                  <span>{fmtAmount(p.summary.total_value)} FCFA</span>
                </div>
                <div className={`pnl ${pnl >= 0 ? "pnl-positive" : "pnl-negative"}`}>
                  {pnl >= 0 ? "+" : ""}
                  {fmtAmount(pnl)} FCFA
                  {p.summary.total_return_pct != null && ` · ${fmtPct(p.summary.total_return_pct)}`}
                  {p.targetReturn != null && (
                    <span className="muted"> · cible {fmtRate(p.targetReturn)}</span>
                  )}
                </div>
              </Link>
            );
          })
        )}
        {data?.length === 0 && <p className="muted">Aucun portefeuille pour l'instant.</p>}
      </Panel>
    </>
  );
}
